/**
 * The single label authority for the Balanced screen (story 135, RN4).
 *
 * All seven surfaces of RN5 — tree leaves and groups, station column cards, the
 * right panel title and its `Subworkflow:` line, the header "Workflow name"
 * field and the column reorder dialog — compose their text here, so the same
 * `code`/`name` pair always renders the same string. If two surfaces can
 * diverge, they will.
 *
 * Deliberately NOT reused from elsewhere:
 * - `codeNameFormat.js` renders `CODE NAME` (space separator) and has nine
 *   consumers in `workflow2.0` that decision D4 leaves out of scope (RN4.3).
 * - `partReferenceLabel.js` is configurable per installation, which RN4.1
 *   forbids here, and yields `code ()` on an empty name, which RN3 forbids.
 *
 * The label is presentation only: it is never a search criterion, a sort key,
 * or a key to locate, match or persist a node (RN4.4). Labels are allowed to
 * repeat between two nodes and are allowed to be empty; a key can be neither.
 *
 * @module balancedLabel
 */

/**
 * Trims a value and tolerates null/undefined, so a whitespace-only field counts
 * as absent.
 *
 * @param {string} value
 * @returns {string}
 */
const clean = (value) => String(value ?? '').trim()

/**
 * Composes the display label of a job or workflow as `CODE (NAME)`.
 *
 * Degradation rules (RN3, RN3.1, RN3.2):
 * - name absent (null, empty or whitespace-only) → the code alone, with no
 *   empty parentheses and no filler text
 * - code absent → the name alone, with no dangling parentheses
 * - both absent → the empty string; the row is still painted, since a job that
 *   cannot be labelled is still work to balance
 * - name identical to the code → `CODE (CODE)`, never collapsed: there is no
 *   collapse rule and none must be added (D-d)
 *
 * Neither field is ever substituted for the other (RN1.1 of story 28).
 *
 * @param {string} code Business identifier of the node
 * @param {string} name Optional free-text name
 * @returns {string} The label, possibly empty
 */
export const formatBalancedLabel = (code, name) => {
  const safeCode = clean(code)
  const safeName = clean(name)
  if (!safeCode) return safeName
  if (!safeName) return safeCode
  return safeCode + ' (' + safeName + ')'
}

export default formatBalancedLabel
